/**
 * Number formatting for the cost dashboard.
 *
 * The hard requirement: $0.0003 and $412.50 have to read well in the SAME
 * column. Plain `Intl.NumberFormat` currency formatting renders the first as
 * `$0.00` — a silent lie that makes per-call cost look free, which is exactly
 * the impression this feature exists to correct.
 *
 * LOCALE IS HARDCODED TO en-US ON PURPOSE, including in Arabic. Three reasons:
 *
 * 1. `ar` locales render Arabic-Indic digits (٤١٢٫٥٠) and USD as `US$`. An ops
 *    person reconciling against an OpenRouter invoice needs the digits to match
 *    the invoice character for character.
 * 2. Arabic-Indic digits in a right-aligned numeric column produce bidi
 *    reordering artefacts around `$` and the minus sign.
 * 3. It matches the existing house stance — `adminFormat.ts` says outright that
 *    its formatters are "locale-agnostic to match the design's terse style".
 *
 * Do not "fix" this by passing `undefined` for the locale.
 */

const EM_DASH = '—';

// Constructed once at module scope: building an Intl.NumberFormat per cell is
// measurably slow in a 100-row table.
const usd = (min: number, max: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  });

const USD_2 = usd(2, 2);
const USD_4 = usd(4, 4);
const USD_6 = usd(6, 6);
const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const PLAIN = new Intl.NumberFormat('en-US');

/**
 * Aggregate money — totals, tenant rows, KPI tiles.
 *
 * Bucketed decimals (not `maximumSignificantDigits`) so a column stays
 * decimal-aligned. Never abbreviated: `$1.2K` is wrong for a figure someone
 * reconciles against an invoice; grouping separators handle the width.
 */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  const abs = Math.abs(value);
  if (value === 0) return USD_2.format(0);
  if (abs >= 0.01) return USD_2.format(value);
  if (abs >= 0.0001) return USD_4.format(value);
  return value > 0 ? '<$0.0001' : '>-$0.0001';
}

/** Per-event money — the drill-down, where a single call can cost 6 decimals. */
export function formatUsdMicro(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  const abs = Math.abs(value);
  if (value === 0) return USD_2.format(0);
  if (abs >= 0.01) return USD_2.format(value);
  if (abs >= 0.000001) return USD_6.format(value);
  return value > 0 ? '<$0.000001' : '>-$0.000001';
}

/** Token counts and call counts — a magnitude, not money, so abbreviate. */
export function formatCompactCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  return value < 1000 ? PLAIN.format(value) : COMPACT.format(value);
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH;
  return PLAIN.format(value);
}

/** A 0..1 ratio as a whole percent. */
export function formatPercent(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return EM_DASH;
  return `${Math.round(ratio * 100)}%`;
}

/**
 * Period-over-period delta, e.g. "+18% vs prev".
 *
 * Returns null when the previous window was zero: "+∞%" is noise, and "+100%"
 * from a zero base is misleading.
 */
export function formatDelta(current: number, previous: number): string | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return '0%';
  return `${pct > 0 ? '+' : ''}${pct}%`;
}

